import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { createFilterOptions } from "@mui/base/useAutocomplete";
import Autocomplete from "@mui/joy/Autocomplete";
import Chip from "@mui/joy/Chip";
import {
  Box,
  Button,
  ChipDelete,
  CircularProgress,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Snackbar,
  Stack,
  Typography,
} from "@mui/joy";
import _ from "lodash";
import { PERMISSIONS } from "../context/AuthContext";
import PermissionGate from "../components/PermissionGate";
import { usePermissions } from "../hooks/usePermissions";
import { deleteShipmentTag, getShipmentTags, postShipmentTag, putShipmentTag } from "../API/shipmentTags";
import {
  getShipmentTagChipInlineStyle,
  getShipmentTagChipSx,
  normalizeTagColor,
  ShipmentTagOptionRow,
} from "./shipmentTagUi";

const DEFAULT_NEW_TAG_COLOR = "#6B7280";
const HEX_COLOR = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
const TAG_COLOR_PRESETS = [
  "#EF4444",
  "#F97316",
  "#EAB308",
  "#22C55E",
  "#14B8A6",
  "#3B82F6",
  "#8B5CF6",
  "#EC4899",
  "#64748B",
  "#111827",
];

/** Free-solo passes raw strings; catalog uses { id, name, color } objects. */
function tagOptionName(option) {
  if (option == null) return "";
  if (typeof option === "string") return option.trim();
  if (typeof option === "object" && option.name != null) return String(option.name).trim();
  return "";
}

function normalizeTagNamesFromValue(newValue) {
  const names = [];
  const seen = new Set();
  if (!Array.isArray(newValue)) return names;
  for (const v of newValue) {
    const name = tagOptionName(v);
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(name);
  }
  return names;
}

/** Tag already known from shipment hint or catalog — do not open "new tag color" on load. */
function isCataloguedTag(meta, hintTag) {
  if (meta != null && meta.id != null) return true;
  if (hintTag == null) return false;
  if (hintTag.id != null) return true;
  if (hintTag.color != null && String(hintTag.color).trim() !== "") return true;
  return false;
}

/**
 * Multi-select shipment tags with catalog autocomplete and free-text new tags.
 * Controlled value is tag name strings (API write shape). Pass hintTags from shipment GET for chip colors before catalog loads.
 */
export default function ShipmentTagsOptionList({
  value = [],
  onChange,
  hintTags,
  disabled = false,
  label,
  placeholder,
  /** List filters: catalog only (no free-text), no color edit; API uses tag ids resolved from names in the parent. */
  filterMode = false,
    /** When false, hides the default “replace on save” hint (e.g. bulk-tags dialog explains behavior separately). */
  showShipmentSaveHint = true,
}) {
  const { t } = useTranslation("ui");
  const { hasPermission } = usePermissions();
  const resolvedLabel = label ?? t("widgets.tags");
  const resolvedPlaceholder = placeholder ?? t("widgets.tagsPlaceholder");
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [catalogOptions, setCatalogOptions] = useState([]);
  const [lookup, setLookup] = useState(() => new Map());
  const [colorDialog, setColorDialog] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletingTag, setDeletingTag] = useState(false);
  const [savingTagColor, setSavingTagColor] = useState(false);
  const [colorSnackbar, setColorSnackbar] = useState({
    open: false,
    message: "",
    color: "danger",
  });

  const prevValueRef = useRef(null);
  const newTagColorQueueRef = useRef([]);

  useEffect(() => {
    if (!Array.isArray(hintTags) || hintTags.length === 0) return;
    setLookup((prev) => {
      const next = new Map(prev);
      for (const t of hintTags) {
        if (t && t.name) {
          next.set(t.name.toLowerCase(), {
            id: t.id,
            name: t.name,
            color: t.color == null ? null : t.color,
          });
        }
      }
      return next;
    });
  }, [hintTags]);

  const fetchTags = useCallback(async (searchTerm = "") => {
    setLoading(true);
    try {
      const res = await getShipmentTags(searchTerm, 1, 100);
      const results = res && res.data && res.data.results;
      const list = Array.isArray(results) ? results : [];
      setCatalogOptions(list);
      setLookup((prev) => {
        const next = new Map(prev);
        for (const t of list) {
          next.set(t.name.toLowerCase(), {
            id: t.id,
            name: t.name,
            color: t.color == null ? null : t.color,
          });
        }
        return next;
      });
    } catch (e) {
      console.error("Failed to fetch shipment tags:", e && e.message);
      setCatalogOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedFetch = useMemo(() => _.debounce((q) => fetchTags(q), 300), [fetchTags]);

  useEffect(() => {
    if (!open) return;
    if (inputValue === "") {
      fetchTags("");
    } else {
      debouncedFetch(inputValue);
    }
    return () => debouncedFetch.cancel();
  }, [inputValue, open, debouncedFetch, fetchTags]);

  const valueObjects = useMemo(() => {
    if (!Array.isArray(value)) return [];
    return value.map((name) => {
      const k = name.toLowerCase();
      return lookup.get(k) || { id: null, name, color: null };
    });
  }, [value, lookup]);

  const displayOptions = useMemo(() => {
    const map = new Map();
    for (const o of catalogOptions) {
      map.set(o.name.toLowerCase(), {
        id: o.id,
        name: o.name,
        color: o.color == null ? null : o.color,
        shipment_count: o.shipment_count,
      });
    }
    for (const name of value) {
      const k = name.toLowerCase();
      if (!map.has(k)) {
        const meta = lookup.get(k) || { id: null, name, color: null };
        map.set(k, meta);
      }
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [catalogOptions, value, lookup]);

  /**
   * Must filter options by what the user typed. Returning all options (no filter) keeps
   * highlightedIndex at 0 whenever the list is open, so keyboard confirm selects the first row
   * instead of creating a free-solo tag (MUI useAutocomplete branch order).
   */
  const filterOptions = useMemo(
    () =>
      createFilterOptions({
        stringify: (option) => tagOptionName(option),
        ignoreCase: true,
        trim: true,
        matchFrom: "any",
      }),
    []
  );

  const handleChange = (event, newValue) => {
    const names = normalizeTagNamesFromValue(newValue);
    if (onChange) onChange(names);
  };

  useEffect(() => {
    if (prevValueRef.current === null) {
      prevValueRef.current = (value || []).slice();
      return;
    }
    const prev = prevValueRef.current;
    const prevLower = new Set(prev.map((n) => n.toLowerCase()));
    const added = (value || []).filter((n) => !prevLower.has(n.toLowerCase()));
    prevValueRef.current = (value || []).slice();

    for (const name of added) {
      const k = name.toLowerCase();
      const meta = lookup.get(k);
      const hintTag =
        Array.isArray(hintTags) && hintTags.find((t) => t && t.name && t.name.toLowerCase() === k);
      if (!isCataloguedTag(meta, hintTag)) {
        newTagColorQueueRef.current.push({ name, color: DEFAULT_NEW_TAG_COLOR });
      }
    }
  }, [value, lookup, hintTags]);

  useEffect(() => {
    if (colorDialog != null) return;
    const next = newTagColorQueueRef.current.shift();
    if (next) {
      setColorDialog({ name: next.name, color: next.color, source: "new" });
    }
  }, [value, lookup, colorDialog]);

  const closeColorDialog = () => {
    setColorDialog(null);
  };

  const handleSaveNewTagColor = async () => {
    if (!colorDialog) return;
    const { name, color } = colorDialog;
    let hex = (color || "").trim();
    if (hex && !hex.startsWith("#")) {
      hex = `#${hex}`;
    }
    if (!HEX_COLOR.test(hex)) {
      setColorSnackbar({
        open: true,
        message: t('widgets.invalidHexColor'),
        color: "danger",
      });
      return;
    }
    const existing = lookup.get(name.toLowerCase());
    const isUpdate = existing && existing.id != null;
    if (isUpdate) {
      if (!hasPermission(PERMISSIONS.SHIPMENT_TAGS_UPDATE)) {
        closeColorDialog();
        return;
      }
    } else if (!hasPermission(PERMISSIONS.SHIPMENT_TAGS_CREATE)) {
      closeColorDialog();
      return;
    }
    setSavingTagColor(true);
    try {
      const res = isUpdate
        ? await putShipmentTag(existing.id, {
            name: existing.name != null ? existing.name : name,
            color: hex,
          })
        : await postShipmentTag({ name, color: hex });
      if (res && res.status >= 200 && res.status < 300) {
        const data = res.data;
        setLookup((prev) => {
          const next = new Map(prev);
          const k = name.toLowerCase();
          next.set(k, {
            id: data.id != null ? data.id : existing != null && existing.id != null ? existing.id : null,
            name: data.name != null ? data.name : name,
            color: data.color == null ? hex : data.color,
          });
          return next;
        });
        closeColorDialog();
        setColorSnackbar({
          open: true,
          message: t("widgets.tagsSnackbarColorSaved"),
          color: "success",
        });
      } else {
        setColorSnackbar({
          open: true,
          message: t("widgets.tagsSnackbarColorSaveFailed"),
          color: "danger",
        });
      }
    } catch (e) {
      const status = e && e.response && e.response.status;
      if (status === 403) {
        setColorSnackbar({
          open: true,
          message: t("widgets.tagsSnackbarColorPermissionDenied"),
          color: "danger",
        });
        closeColorDialog();
      } else {
        setColorSnackbar({
          open: true,
          message: t("widgets.tagsSnackbarColorSaveError"),
          color: "danger",
        });
      }
    } finally {
      setSavingTagColor(false);
    }
  };

  const handleSkipNewTagColor = () => {
    closeColorDialog();
  };

  const openEditTagColor = (meta, displayName) => {
    const name = meta && meta.name != null ? meta.name : displayName;
    const raw = meta && meta.color != null && String(meta.color).trim() !== "" ? meta.color : DEFAULT_NEW_TAG_COLOR;
    const normalized = normalizeTagColor(raw);
    setColorDialog({
      name,
      color: normalized || raw,
      source: "edit",
    });
  };

  const removeCatalogTagFromLocalState = useCallback(
    (id, name) => {
      const k = name.toLowerCase();
      setLookup((prev) => {
        const next = new Map(prev);
        next.delete(k);
        return next;
      });
      setCatalogOptions((prev) => prev.filter((t) => t.id !== id));
      if (onChange && Array.isArray(value)) {
        const nextNames = value.filter((n) => n.toLowerCase() !== k);
        if (nextNames.length !== value.length) {
          prevValueRef.current = nextNames.slice();
          onChange(nextNames);
        }
      }
    },
    [onChange, value]
  );

  const handleConfirmDeleteCatalogTag = async () => {
    if (!deleteConfirm) return;
    if (!hasPermission(PERMISSIONS.SHIPMENT_TAGS_DELETE)) {
      setDeleteConfirm(null);
      return;
    }
    const { id, name } = deleteConfirm;
    setDeletingTag(true);
    try {
      const res = await deleteShipmentTag(id);
      if (res && res.status >= 200 && res.status < 300) {
        removeCatalogTagFromLocalState(id, name);
        setDeleteConfirm(null);
        setColorSnackbar({
          open: true,
          message: t("widgets.tagsSnackbarTagRemoved"),
          color: "success",
        });
        fetchTags(inputValue || "");
      } else {
        setColorSnackbar({
          open: true,
          message: t("widgets.tagsSnackbarDeleteFailed"),
          color: "danger",
        });
      }
    } catch (e) {
      const status = e && e.response && e.response.status;
      if (status === 403) {
        setColorSnackbar({
          open: true,
          message: t("widgets.tagsSnackbarDeletePermissionDenied"),
          color: "danger",
        });
      } else if (status === 404) {
        removeCatalogTagFromLocalState(id, name);
        setDeleteConfirm(null);
        setColorSnackbar({
          open: true,
          message: t("widgets.tagsSnackbarTagAlreadyRemoved"),
          color: "success",
        });
        fetchTags(inputValue || "");
      } else {
        setColorSnackbar({
          open: true,
          message: t("widgets.tagsSnackbarDeleteFailed"),
          color: "danger",
        });
      }
    } finally {
      setDeletingTag(false);
    }
  };

  return (
    <Box sx={{ marginBottom: 2 }}>
      <FormLabel
        sx={{
          fontWeight: 600,
          color: "#555",
          marginBottom: "6px",
          fontSize: "14px",
        }}
      >
        {resolvedLabel}
      </FormLabel>
      <Autocomplete
        multiple
        freeSolo={!filterMode}
        autoHighlight={false}
        size="md"
        placeholder={filterMode ? t("widgets.searchCatalogTags") : resolvedPlaceholder}
        options={displayOptions}
        value={valueObjects}
        loading={loading}
        disabled={disabled}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        inputValue={inputValue}
        onInputChange={(event, newInputValue, reason) => {
          // Catalog fetch updates options/value metadata; MUI then emits reason "reset"
          // and would wipe the in-progress search unless we ignore it here.
          if (reason === "reset") return;
          setInputValue(newInputValue);
        }}
        onChange={handleChange}
        disableCloseOnSelect
        isOptionEqualToValue={(option, val) => {
          const a = tagOptionName(option);
          const b = tagOptionName(val);
          if (!a || !b) return false;
          return a.toLowerCase() === b.toLowerCase();
        }}
        getOptionLabel={(option) => tagOptionName(option)}
        filterOptions={filterOptions}
        noOptionsText={
          loading
            ? t("widgets.tagsNoOptionsLoading")
            : filterMode
              ? t("widgets.tagsNoOptionsNoMatch")
              : t("widgets.tagsNoOptionsHint")
        }
        endDecorator={loading ? <CircularProgress size="sm" sx={{ bgcolor: "background.surface" }} /> : null}
        renderOption={(props, option) => {
          const label = tagOptionName(option);
          const oid = typeof option === "object" && option != null ? option.id : null;
          const k = label.toLowerCase();
          const fromLookup = lookup.get(k);
          const tagColor =
            fromLookup != null && fromLookup.color != null && fromLookup.color !== ""
              ? fromLookup.color
              : typeof option === "object" && option != null
                ? option.color
                : null;
          const count =
            typeof option === "object" && option != null && option.shipment_count != null
              ? option.shipment_count
              : null;
          const meta =
            fromLookup != null
              ? fromLookup
              : oid != null
                ? {
                    id: oid,
                    name: label,
                    color: typeof option === "object" && option != null ? option.color : null,
                  }
                : null;
          const rowProps = { name: label, color: tagColor, shipmentCount: count };
          const onEditColor =
            !filterMode && !disabled && meta != null && meta.id != null
              ? () => openEditTagColor(meta, label)
              : undefined;
          const onDeleteCatalog =
            !filterMode && !disabled && meta != null && meta.id != null
              ? () => setDeleteConfirm({ id: meta.id, name: label })
              : undefined;
          return (
            <li {...props} key={oid != null ? oid : label}>
              <PermissionGate
                permission={PERMISSIONS.SHIPMENT_TAGS_UPDATE}
                fallback={
                  <PermissionGate
                    permission={PERMISSIONS.SHIPMENT_TAGS_DELETE}
                    fallback={<ShipmentTagOptionRow {...rowProps} />}
                  >
                    <ShipmentTagOptionRow {...rowProps} onDelete={onDeleteCatalog} />
                  </PermissionGate>
                }
              >
                <PermissionGate
                  permission={PERMISSIONS.SHIPMENT_TAGS_DELETE}
                  fallback={<ShipmentTagOptionRow {...rowProps} onEditColor={onEditColor} />}
                >
                  <ShipmentTagOptionRow {...rowProps} onEditColor={onEditColor} onDelete={onDeleteCatalog} />
                </PermissionGate>
              </PermissionGate>
            </li>
          );
        }}
        renderTags={(tags, getTagProps) =>
          tags.map((item, index) => {
            const name = tagOptionName(item);
            const k = name.toLowerCase();
            const meta =
              typeof item === "object" && item != null && item.name != null ? item : lookup.get(k);
            const chipColor =
              meta != null && meta.color != null && meta.color !== "" ? meta.color : null;
            const inlineStyle = getShipmentTagChipInlineStyle(chipColor);
            const tagProps = getTagProps({ index });
            const chipDeleteProps = _.omit(tagProps, "key");
            return (
              <Chip
                key={name}
                variant="plain"
                style={inlineStyle}
                sx={{
                  ...getShipmentTagChipSx(chipColor),
                  minWidth: 0,
                }}
                endDecorator={<ChipDelete {...chipDeleteProps} />}
              >
                {name}
              </Chip>
            );
          })
        }
        slotProps={{
          listbox: {
            sx: {
              mt: "6px",
              py: 0.75,
              px: 0.75,
              bgcolor: "background.popup",
              backgroundImage: "none",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "md",
              boxShadow: "lg",
              maxHeight: "min(40vh, 380px)",
              overflow: "auto",
              zIndex: 1300,
              "& [role='option']": {
                borderRadius: "sm",
                my: 0.125,
                mx: 0,
                px: 1,
                py: 0.65,
                alignItems: "center",
                bgcolor: "transparent",
                border: "1px solid transparent",
                boxSizing: "border-box",
                transition: "background-color 0.12s ease, border-color 0.12s ease",
              },
              "& [role='option']:hover": {
                bgcolor: "background.level1",
                borderColor: "neutral.outlinedHoverBorder",
              },
              "& [role='option'][aria-selected='true']": {
                bgcolor: "primary.softBg",
                borderColor: "primary.outlinedBorder",
              },
            },
          },
        }}
        sx={{
          backgroundColor: "#f7f9fc",
          borderRadius: "8px",
          padding: "4px",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          "&:hover": { borderColor: "#5f09c2" },
          "&:focus-within": {
            borderColor: "#5f09c2",
            outline: "none",
            boxShadow: "0 0 6px rgba(0, 123, 255, 0.5)",
          },
          "&[disabled]": {
            backgroundColor: "#e9ecef",
            color: "#6c757d",
            cursor: "not-allowed",
          },
        }}
      />
      {filterMode ? (
        <Typography level="body-xs" sx={{ mt: 0.75, color: "neutral.600" }}>
        </Typography>
      ) : showShipmentSaveHint ? (
        <Typography level="body-xs" sx={{ mt: 0.75, color: "neutral.600" }}>
          {t("widgets.tagsHintReplaceOnSave")}
          <PermissionGate permission={PERMISSIONS.SHIPMENT_TAGS_UPDATE} fallback={null}>
            {" "}
            {t("widgets.tagsHintCatalogChange")}
          </PermissionGate>
          <PermissionGate permission={PERMISSIONS.SHIPMENT_TAGS_DELETE} fallback={null}>
            {" "}
            {t("widgets.tagsHintCatalogTrash")}
          </PermissionGate>
        </Typography>
      ) : (
        <Typography level="body-xs" sx={{ mt: 0.75, color: "neutral.600" }}>
          {t("widgets.tagsHintShort")}
        </Typography>
      )}

      <Modal
        open={colorDialog != null}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            handleSkipNewTagColor();
          }
        }}
      >
        <ModalDialog
          role="dialog"
          aria-labelledby="tag-color-dialog-title"
          sx={{ maxWidth: 420, width: "100%" }}
        >
          <Typography id="tag-color-dialog-title" level="title-lg" sx={{ mb: 1 }}>
            {colorDialog && colorDialog.source === "edit"
              ? t("widgets.tagsDialogTitleEdit")
              : t("widgets.tagsDialogTitleNew")}
          </Typography>
          {colorDialog && (
            <>
              <Typography level="body-sm" sx={{ mb: 2, color: "neutral.600" }}>
                {colorDialog.source === "edit" ? (
                  <Trans
                    i18nKey="widgets.tagsDialogEditCatalogBody"
                    values={{ name: colorDialog.name }}
                    components={{ strong: <strong /> }}
                  />
                ) : (
                  <Trans
                    i18nKey="widgets.tagsDialogNewTagBody"
                    values={{ name: colorDialog.name }}
                    components={{ strong: <strong /> }}
                  />
                )}
              </Typography>

              <Typography level="body-xs" sx={{ mb: 0.75, fontWeight: 600 }}>
                {t("widgets.tagsQuickColors")}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                {TAG_COLOR_PRESETS.map((hex) => (
                  <Box
                    key={hex}
                    component="button"
                    type="button"
                    onClick={() => setColorDialog((d) => (d ? { ...d, color: hex } : d))}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      border: "2px solid",
                      borderColor: colorDialog.color === hex ? "#5f09c2" : "neutral.300",
                      bgcolor: hex,
                      cursor: "pointer",
                      p: 0,
                      flexShrink: 0,
                    }}
                    aria-label={t("widgets.tagsColorPresetAria", { hex })}
                  />
                ))}
              </Stack>

              <Typography level="body-xs" sx={{ mb: 0.75, fontWeight: 600 }}>
                {t("widgets.tagsCustomColor")}
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Box
                  component="input"
                  type="color"
                  value={colorDialog.color || DEFAULT_NEW_TAG_COLOR}
                  onChange={(e) =>
                    setColorDialog((d) => (d ? { ...d, color: e.target.value } : d))
                  }
                  sx={{
                    width: 56,
                    height: 40,
                    padding: 0,
                    border: "1px solid",
                    borderColor: "neutral.300",
                    borderRadius: "8px",
                    cursor: "pointer",
                    bgcolor: "background.body",
                  }}
                />
                <Input
                  value={colorDialog.color || ""}
                  onChange={(e) =>
                    setColorDialog((d) => (d ? { ...d, color: e.target.value } : d))
                  }
                  placeholder={t("widgets.tagsHexPlaceholder")}
                  sx={{ flex: 1 }}
                />
              </Stack>

              <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                <Button variant="plain" color="neutral" onClick={handleSkipNewTagColor} disabled={savingTagColor}>
                  {colorDialog.source === "edit"
                    ? t("widgets.tagsDialogCancel")
                    : t("widgets.tagsDialogSkipForNow")}
                </Button>
                <Button loading={savingTagColor} onClick={handleSaveNewTagColor}>
                  {t("widgets.tagsDialogSaveColor")}
                </Button>
              </Stack>
            </>
          )}
        </ModalDialog>
      </Modal>

      <Modal
        open={deleteConfirm != null}
        onClose={(_, reason) => {
          if (
            !deletingTag &&
            (reason === "backdropClick" || reason === "escapeKeyDown")
          ) {
            setDeleteConfirm(null);
          }
        }}
      >
        <ModalDialog
          role="alertdialog"
          aria-labelledby="delete-tag-dialog-title"
          variant="outlined"
          sx={{ maxWidth: 420, width: "100%" }}
        >
          <Typography id="delete-tag-dialog-title" level="title-lg" sx={{ mb: 1 }}>
            {t("widgets.tagsDeleteDialogTitle")}
          </Typography>
          <Typography level="body-sm" sx={{ mb: 2, color: "neutral.600" }}>
            <Trans
              i18nKey="widgets.tagsDeleteDialogBody"
              values={{ name: deleteConfirm != null ? deleteConfirm.name : "" }}
              components={{ strong: <strong /> }}
            />
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setDeleteConfirm(null)}
              disabled={deletingTag}
            >
              {t("actions.cancel")}
            </Button>
            <Button color="danger" loading={deletingTag} onClick={handleConfirmDeleteCatalogTag}>
              {t("widgets.tagsDeleteConfirm")}
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      <Snackbar
        open={colorSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setColorSnackbar((s) => ({ ...s, open: false }))}
        variant="soft"
        color={colorSnackbar.color === "success" ? "success" : "danger"}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        {colorSnackbar.message}
      </Snackbar>
    </Box>
  );
}

ShipmentTagsOptionList.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  hintTags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      color: PropTypes.string,
    })
  ),
  disabled: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  filterMode: PropTypes.bool,
  showShipmentSaveHint: PropTypes.bool,
};
